import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  MessageCircle, 
  Phone, 
  User, 
  Calendar, 
  Clock, 
  CheckCircle,
  Send,
  Paperclip,
  Smile,
  MoreVertical,
  Search,
  Filter,
  Plus,
  Star,
  Archive,
  Bell
} from 'lucide-react';

const WhatsAppCRM = () => {
  const [selectedContact, setSelectedContact] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const contacts = [
    {
      id: 1,
      name: 'Ahmet Yılmaz',
      phone: '+90 532 123 4567',
      lastMessage: 'E-ticaret paketi hakkında bilgi alabilir miyim?',
      timestamp: '10:30',
      unreadCount: 2,
      status: 'online',
      type: 'prospect',
      tags: ['E-ticaret', 'Potansiyel Müşteri'],
      projectValue: '₺15,000',
      stage: 'Tekliffi Bekliyor'
    },
    {
      id: 2,
      name: 'Zeynep Kaya',
      phone: '+90 533 456 7890',
      lastMessage: 'Hosting paketi aldım, teşekkürler!',
      timestamp: '09:45',
      unreadCount: 0,
      status: 'online',
      type: 'customer',
      tags: ['Hosting', 'Müşteri'],
      projectValue: '₺2,400',
      stage: 'Aktif Müşteri'
    },
    {
      id: 3,
      name: 'Mehmet Özkan',
      phone: '+90 534 789 0123',
      lastMessage: 'Logo tasarımı için freelancer arıyorum',
      timestamp: 'Dün',
      unreadCount: 1,
      status: 'offline',
      type: 'prospect',
      tags: ['Tasarım', 'Logo'],
      projectValue: '₺3,500',
      stage: 'İlk Görüşme'
    },
    {
      id: 4,
      name: 'Fatma Demir',
      phone: '+90 535 012 3456',
      lastMessage: 'Proje tamamlandı, çok memnunum',
      timestamp: '2 gün önce',
      unreadCount: 0,
      status: 'offline',
      type: 'customer',
      tags: ['Web Geliştirme', 'Tamamlandı'],
      projectValue: '₺12,000',
      stage: 'Proje Tamamlandı'
    }
  ];

  const messages = selectedContact ? [
    {
      id: 1,
      sender: 'contact',
      content: 'Merhaba, e-ticaret paketi hakkında bilgi alabilir miyim?',
      timestamp: '10:25',
      type: 'text'
    },
    {
      id: 2,
      sender: 'agent',
      content: 'Merhaba! Tabii ki. Hangi büyüklükte bir işletmeniz var?',
      timestamp: '10:26',
      type: 'text'
    },
    {
      id: 3,
      sender: 'contact',
      content: 'Orta ölçekli bir işletmem var, aylık 500-1000 ürün satışı yapıyoruz.',
      timestamp: '10:28',
      type: 'text'
    },
    {
      id: 4,
      sender: 'agent',
      content: 'Size Professional B2B paketimizi önerebilirim. Aylık ₺2999 ve tüm özellikler dahil.',
      timestamp: '10:29',
      type: 'text'
    },
    {
      id: 5,
      sender: 'contact',
      content: 'Hangi özellikler var? Detaylı bilgi verebilir misiniz?',
      timestamp: '10:30',
      type: 'text'
    }
  ] : [];

  const quickResponses = [
    'Merhaba! Size nasıl yardımcı olabilirim?',
    'Paket bilgilerini hemen gönderiyorum.',
    'Demo için randevu ayarlayalım.',
    'Teklifimizi hazırladık, inceleyin.',
    'Teşekkürler, iyi günler!'
  ];

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim() && selectedContact) {
      console.log('Sending WhatsApp message:', newMessage);
      setNewMessage('');
    }
  };

  const getStatusColor = (status) => {
    return status === 'online' ? 'bg-green-500' : 'bg-gray-400';
  };

  const getTypeColor = (type) => {
    return type === 'customer' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700';
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-green-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">WhatsApp CRM</h1>
                <p className="text-green-100">Müşteri İletişim Merkezi</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-white/20 text-white">
                {filteredContacts.length} Kontak
              </Badge>
              <Button className="bg-white text-green-600 hover:bg-gray-100">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Kontak
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-6 h-[600px]">
          {/* Contacts Sidebar */}
          <div className="col-span-4 bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Kontak ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Contacts List */}
            <div className="overflow-y-auto h-full">
              {filteredContacts.map((contact, index) => (
                <div
                  key={contact.id}
                  onClick={() => setSelectedContact(contact)}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-all duration-300 hover:bg-green-50 ${
                    selectedContact?.id === contact.id ? 'bg-green-100 border-l-4 border-l-green-600' : ''
                  }`}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    animation: 'fadeInLeft 0.6s ease-out forwards'
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={`/avatar-${contact.id}.jpg`} />
                        <AvatarFallback className="bg-green-100 text-green-600 font-semibold">
                          {contact.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 ${getStatusColor(contact.status)} border-2 border-white rounded-full`}></div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 truncate">{contact.name}</h3>
                        <div className="flex items-center space-x-2">
                          {contact.unreadCount > 0 && (
                            <Badge className="bg-green-600 text-white min-w-[20px] h-5 text-xs">
                              {contact.unreadCount}
                            </Badge>
                          )}
                          <span className="text-xs text-gray-500">{contact.timestamp}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge className={getTypeColor(contact.type)} variant="secondary">
                          {contact.type === 'customer' ? 'Müşteri' : 'Potansiyel'}
                        </Badge>
                        <span className="text-xs text-gray-500">{contact.projectValue}</span>
                      </div>
                      
                      <p className="text-sm text-gray-600 truncate mt-1">{contact.lastMessage}</p>
                      
                      <div className="flex flex-wrap gap-1 mt-2">
                        {contact.tags.slice(0, 2).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat Area */}
          <div className="col-span-8 bg-white rounded-xl shadow-lg overflow-hidden flex flex-col">
            {selectedContact ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 bg-green-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={`/avatar-${selectedContact.id}.jpg`} />
                          <AvatarFallback className="bg-green-100 text-green-600 font-semibold">
                            {selectedContact.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className={`absolute -bottom-1 -right-1 w-4 h-4 ${getStatusColor(selectedContact.status)} border-2 border-white rounded-full`}></div>
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{selectedContact.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="flex items-center space-x-1">
                            <Phone className="w-4 h-4" />
                            <span>{selectedContact.phone}</span>
                          </span>
                          <span>{selectedContact.stage}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="icon">
                        <Phone className="w-5 h-5" />
                      </Button>
                      <Button variant="ghost" size="icon">
                        <Star className="w-5 h-5" />
                      </Button>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.map((message, index) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender === 'agent' ? 'justify-end' : 'justify-start'}`}
                      style={{
                        animationDelay: `${index * 0.1}s`,
                        animation: 'fadeInUp 0.4s ease-out forwards'
                      }}
                    >
                      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                        message.sender === 'agent' 
                          ? 'bg-green-600 text-white rounded-br-sm' 
                          : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
                      }`}>
                        <p className="text-sm leading-relaxed">{message.content}</p>
                        <div className={`flex items-center justify-end mt-1 space-x-1 ${
                          message.sender === 'agent' ? 'text-green-200' : 'text-gray-500'
                        }`}>
                          <span className="text-xs">{message.timestamp}</span>
                          {message.sender === 'agent' && (
                            <CheckCircle className="w-3 h-3" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick Responses */}
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <div className="flex space-x-2 overflow-x-auto">
                    {quickResponses.map((response, idx) => (
                      <button
                        key={idx}
                        onClick={() => setNewMessage(response)}
                        className="px-3 py-1 text-xs bg-white border border-gray-300 rounded-full hover:bg-green-50 hover:border-green-300 transition-all duration-300 whitespace-nowrap"
                      >
                        {response}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-gray-200 bg-white">
                  <form onSubmit={handleSendMessage} className="flex items-center space-x-4">
                    <Button variant="ghost" size="icon" type="button">
                      <Paperclip className="w-5 h-5" />
                    </Button>
                    
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        placeholder="WhatsApp mesajı yazın..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="pr-12 h-12 rounded-full border-gray-300 focus:border-green-500"
                      />
                      <Button variant="ghost" size="icon" type="button" className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <Smile className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="bg-green-600 hover:bg-green-700 rounded-full p-3"
                      disabled={!newMessage.trim()}
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              /* No Contact Selected */
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center space-y-6">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <MessageCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-gray-900">WhatsApp CRM</h3>
                    <p className="text-gray-600">Sol taraftan bir kontak seçin ve sohbete başlayın</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
          {[
            { title: 'Toplam Kontak', value: '156', color: 'from-green-500 to-emerald-500', icon: User },
            { title: 'Aktif Görüşmeler', value: '42', color: 'from-blue-500 to-cyan-500', icon: MessageCircle },
            { title: 'Bu Ay Müşteri', value: '28', color: 'from-purple-500 to-pink-500', icon: CheckCircle },
            { title: 'Toplam Satış', value: '₺125K', color: 'from-orange-500 to-red-500', icon: Calendar }
          ].map((stat, index) => (
            <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

    </div>
  );
};

export default WhatsAppCRM;